import React, { useEffect, useState } from 'react';
import {
  Box,
  BoxProps,
  ButtonBase,
  Popover,
  Typography,
  TypographyProps,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useAppSelector } from '../../../../hooks';
import { getSubjectColorNew } from '../../../../helper';

import { getTopicState } from '../../../../store/TopicSlice';
import { ISubTopicForProgress } from '../../../../store/ProgressSlice';

import CircularProgressWithValue from '../../UserDashboard/components/CircularProgressWithValue';

const progressTextProps: TypographyProps = {
  variant: 'subtitle2',
  textAlign: 'left',
};

const containerStyles: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
};

const boxProps: BoxProps = {
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const CircularProgressWithContent = ({ content, value, subjectColor }) => {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgressWithValue
        value={value || 0}
        progressProps={{ color: '#22C55E' }}
        thickness={3}
        circleColor="#E2E8F0"
      />
      <Box {...boxProps}>
        <Typography variant="caption" component="div" color="textSecondary">
          {content}
        </Typography>
      </Box>
    </Box>
  );
};

interface SubTopicProgressProps {
  currentProgress: ISubTopicForProgress | null;
}

const SubTopicProgress: React.FC<SubTopicProgressProps> = ({
  currentProgress,
}) => {
  const topicState = useAppSelector(getTopicState);
  const { topicsWithSubTopics } = topicState;

  // State
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [completionPercentage, setCompletionPercentage] = useState<number>(0);
  const [remainingText, setRemainingText] = useState<string>(
    '3 tasks remaining'
  );

  const subjectColor = getSubjectColorNew(
    topicsWithSubTopics.length ? topicsWithSubTopics[0].subject : ''
  );

  useEffect(() => {
    if (currentProgress) {
      const MAX_RESOURCE = 34;
      const MAX_QUIZ = 33;
      const MAX_PRACTICE = 33;

      setCompletionPercentage(currentProgress.completion ?? 0);

      const remainingParts =
        (currentProgress.resourcePart === undefined ||
        currentProgress.resourcePart < MAX_RESOURCE
          ? 1
          : 0) +
        (currentProgress.quizPart === undefined ||
        currentProgress.quizPart < MAX_QUIZ
          ? 1
          : 0) +
        (currentProgress.practicePart === undefined ||
        currentProgress.practicePart < MAX_PRACTICE
          ? 1
          : 0);

      const label =
        remainingParts === 0
          ? '0 tasks remaining'
          : `${remainingParts} task${remainingParts > 1 ? 's' : ''} remaining`;

      setRemainingText(label);
    }
  }, [currentProgress]);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'progress-popover' : undefined;

  const progressItems = [
    {
      label: 'Resources & Exam Tips',
      isComplete: currentProgress?.resourcePart === 34,
    },
    {
      label: 'Practice Questions',
      isComplete: currentProgress?.practicePart === 33,
    },
    {
      label: 'Quiz',
      isComplete: currentProgress?.quizPart === 33,
    },
  ];

  return (
    <>
      <div style={containerStyles}>
        <ButtonBase
          disableRipple
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
        >
          <CircularProgressWithContent
            value={completionPercentage}
            content="📐"
            subjectColor={subjectColor}
          />

          <>
            <Box sx={{ marginLeft: '5px' }}>
              <Typography {...progressTextProps} sx={{ fontSize: '16px' }}>
                {Math.round(completionPercentage)}% Complete
              </Typography>
              <Typography
                {...progressTextProps}
                sx={{ fontSize: '12px', color: '#64748B' }}
              >
                {remainingText}
              </Typography>
            </Box>
          </>
        </ButtonBase>
      </div>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          pointerEvents: 'none',
          '& .MuiPaper-root': {
            borderRadius: '20px',
            boxShadow: 'none',
          },
        }}
        style={{ marginTop: '15px' }}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
      >
        <Box sx={{ p: 2, width: '250px' }}>
          {progressItems.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: index < progressItems.length - 1 ? 2 : 0,
              }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: 16,
                  color: item.isComplete ? '#22C55E' : '#E2E8F0',
                }}
              />
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  fontFamily: 'inherit',
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};

export default SubTopicProgress;
